import { useMutation } from '@tanstack/react-query'
import { useContext } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/elements/form'
import { PasswordInput } from '@/components/PasswordInput'
import { updateUserPassword } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast, verifyPassword } from '@/utils/helpers'

import { SharedCardLayout } from './layout'

const PasswordSection = () => {
  const { user } = useContext(UserStoreContext)

  const handleSuccess = (message) => toast(message, 'success')
  const handleError = (message) => toast(message, 'error')

  const { isPending: loading, mutate: mutatePassword } = useMutation({
    mutationFn: (data) => updateUserPassword(data),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Password has been changed.')
      form.reset({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
    },
  })

  const form = useForm({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  return (
    <SharedCardLayout
      title="Change Password"
      description="To change your password, you must provide your current password."
    >
      <SharedCardLayout.Form
        formProps={form}
        onSubmit={form.handleSubmit((data) => mutatePassword({ id: user.id, ...data }))}
      >
        <SharedCardLayout.Fields className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <FormField
              control={form.control}
              disabled={loading}
              name="oldPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      data-testid="oldPassword"
                      id="oldPassword"
                      placeholder="Current Password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: true }}
            />
          </div>

          <div className="col-span-1 space-y-6">
            <FormField
              control={form.control}
              disabled={loading}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      data-testid="newPassword"
                      id="newPassword"
                      placeholder="New Password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: true, validate: (value) => verifyPassword(value) }}
            />

            <FormField
              control={form.control}
              disabled={loading}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      data-testid="confirmPassword"
                      id="confirmPassword"
                      placeholder="Confirm New Password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{
                required: true,
                validate: (value) => {
                  const err = verifyPassword(value)
                  if (err) return err
                  if (form.watch('newPassword') !== value) {
                    return 'Passwords do not match'
                  }
                  return undefined
                },
              }}
            />
          </div>
        </SharedCardLayout.Fields>

        <SharedCardLayout.Footer>
          <Button type="submit" disabled={loading || !form.formState.isDirty} loading={loading}>
            Save Changes
          </Button>
        </SharedCardLayout.Footer>
      </SharedCardLayout.Form>
    </SharedCardLayout>
  )
}

export default PasswordSection
